// https://zustand.docs.pmnd.rs/guides/initialize-state-with-props#extracting-context-logic-into-a-custom-hook

import type { OmitFunctions } from '@seen/core/types'
import {
  createContext,
  type JSX,
  type PropsWithChildren,
  useContext,
  useRef,
} from 'react'
import { type StoreApi, useStore } from 'zustand'

export interface ZustandContext<T extends Record<string, unknown>> {
  Provider: (
    props: PropsWithChildren<{ initialState?: Partial<OmitFunctions<T>> }>
  ) => JSX.Element
  useContextStore: <U>(selector: (state: T) => U) => U
}

export function createZustandContext<T extends Record<string, unknown>>(
  createState: (initialState?: Partial<OmitFunctions<T>>) => StoreApi<T>
): ZustandContext<T> {
  const StoreContext = createContext<StoreApi<T> | null>(null)

  function Provider({
    children,
    initialState,
  }: PropsWithChildren<{
    initialState?: Partial<OmitFunctions<T>>
  }>): JSX.Element {
    const storeRef = useRef<StoreApi<T>>(undefined)
    if (!storeRef.current) {
      storeRef.current = createState(initialState)
    }
    return (
      <StoreContext.Provider value={storeRef.current}>
        {children}
      </StoreContext.Provider>
    )
  }

  function useContextStore<U>(selector: (state: T) => U): U {
    const store = useContext(StoreContext)
    if (!store) {
      throw new Error('Missing StoreContext.Provider in the tree')
    }
    return useStore(store, selector)
  }

  return {
    Provider,
    useContextStore,
  }
}
